import { useEffect, useRef, useState } from "react";
import {
  changeTwoFactorPassword,
  globalAccount,
  sendTwoFactorOtp,
  verifyTwoFactorOtp,
  loadRecaptcha,
} from "../../../api";
import AuthCard, { IconBadge, Notice } from "./components/AuthCard";
import { MailIcon, WhatsAppIcon } from "./components/Icons";
import Stepper from "./components/Stepper";
import {
  API_CHANNEL,
  CHANNEL_LABELS,
  CHANNELS,
  EMAIL_OTP,
  ERROR_DISPLAY_MS,
  FLOWS,
  LOCK_MINUTES,
  MAX_OTP_ATTEMPTS,
  MAX_RESENDS,
  MOBILE_OTP,
  STEPS,
} from "./constants";
import AlternateNumber from "./screens/AlternateNumber";
import ChooseChannel from "./screens/ChooseChannel";
import OtpLocked from "./screens/OtpLocked";
import OtpStep from "./screens/OtpStep";
import PasswordUpdated from "./screens/PasswordUpdated";
import RequestReceived from "./screens/RequestReceived";
import SetNewPassword from "./screens/SetNewPassword";
import { identifyClarityUser } from "../../../Utils/clarity";

// Stepper position (Flow A only; Flow B has no stepper)
const STEP_INDEX = {
  [STEPS.VERIFY_EMAIL]: 0,
  [STEPS.CHOOSE_CHANNEL]: 1,
  [STEPS.ENTER_OTP]: 1,
  [STEPS.ALTERNATE_NUMBER]: 1,
  [STEPS.NEW_PASSWORD]: 2,
};

// TODO(api): placeholder for network latency — remove once the alternate-number request is wired
const placeholderCall = () => new Promise((resolve) => setTimeout(resolve, 600));

// The login response decides the flow: twoFactorEnrolled false (or missing) → A (starts
// with email verification), true → B (starts at the channel screen)
const getFlow = (context) =>
  context.twoFactorEnrolled === true ? FLOWS.RETURNING : FLOWS.FIRST_TIME;

const getFirstStep = (flow) =>
  flow === FLOWS.FIRST_TIME ? STEPS.VERIFY_EMAIL : STEPS.CHOOSE_CHANNEL;

// Only WhatsApp is offered for now
const CHANNEL = CHANNELS.WHATSAPP;

// Responses may be flat ({ success, authToken, ... }) or wrapped ({ code, data })
const isOk = (resp) =>
  resp?.success === true || resp?.code === 200 || resp?.status === 200;

const getPayload = (resp) =>
  resp?.data && typeof resp.data === "object" ? resp.data : resp || {};

/**
 * Login 2FA flow, shown on the login page after a successful login.
 *  - context: the login response data (challengeId, masked email/phone, twoFactorEnrolled)
 *  - onBackToLogin: returns the user to the login form
 *  - onSuccess: called with the login response once the session is ready
 */
function TwoFactor({ context, onBackToLogin, onSuccess }) {
  const [flow] = useState(() => getFlow(context));
  const isFirstTime = flow === FLOWS.FIRST_TIME;
  const { challengeId, maskedEmail, maskedPhone: maskedMobile } = context;

  const [step, setStep] = useState(() => getFirstStep(flow));
  const [otpError, setOtpError] = useState("");
  const [otpErrorCount, setOtpErrorCount] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_OTP_ATTEMPTS);
  const [resendCount, setResendCount] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(
    () => new Date(Date.now() + LOCK_MINUTES * 60 * 1000),
  );
  const [alternateRequest, setAlternateRequest] = useState({
    mobile: "+919987654871",
    referenceId: "BLR-MOB-104578",
  });
  const [passwordError, setPasswordError] = useState("");
  // Request-level failures (network / server), shown above the current screen
  const [requestError, setRequestError] = useState("");
  // Flow A keeps the OTP-verify response until the new password is set
  const pendingSession = useRef(null);
  const sessionStarted = useRef(false);

  useEffect(() => {
    loadRecaptcha();
  }, []);

  // Request errors fade after a few seconds; a new error restarts the timer
  useEffect(() => {
    if (!requestError) return undefined;
    const id = setTimeout(() => setRequestError(""), ERROR_DISPLAY_MS);
    return () => clearTimeout(id);
  }, [requestError]);

  const header =
    isFirstTime && STEP_INDEX[step] !== undefined ? (
      <Stepper current={STEP_INDEX[step]} />
    ) : null;

  const resetOtpState = () => {
    setOtpError("");
    setAttemptsLeft(MAX_OTP_ATTEMPTS);
  };

  const goToLogin = () => onBackToLogin();

  const lockAccount = () => {
    // The lock is enforced by the backend; this is the client-side message window
    setLockedUntil(new Date(Date.now() + LOCK_MINUTES * 60 * 1000));
    setStep(STEPS.LOCKED);
  };

  const handleIncorrectOtp = () => {
    const left = attemptsLeft - 1;
    setAttemptsLeft(left);
    if (left <= 0) {
      lockAccount();
      return;
    }
    setOtpError(`Incorrect OTP. ${left} ${left === 1 ? "attempt" : "attempts"} left.`);
    setOtpErrorCount((count) => count + 1);
  };

  // Send an OTP on a channel (POST /v3/auth/2fa/send-otp); returns whether it was sent.
  // The first email OTP is sent by the backend during login, so nothing is sent on open.
  const sendOtp = async (channel) => {
    setRequestError("");
    try {
      await sendTwoFactorOtp({ challengeId, channel });
      return true;
    } catch (error) {
      setRequestError(error?.message || "Couldn't send the code. Please try again.");
      return false;
    }
  };

  // Verify an OTP (POST /v1/auth/2fa/verify-otp).
  // Returns { ok, resp }; `failed` means the request itself failed (not a wrong code).
  const verifyOtp = async (channel, otp) => {
    setRequestError("");
    try {
      const resp = await verifyTwoFactorOtp({ challengeId, channel, otp });
      return { ok: isOk(resp), resp };
    } catch (error) {
      // A response body from the API means the code was rejected; an Error means no response
      if (error instanceof Error) {
        setRequestError("Couldn't verify the code. Please try again.");
        return { ok: false, failed: true };
      }
      return { ok: false };
    }
  };

  const resendFor = (channel) => async () => {
    if (resendCount >= MAX_RESENDS) {
      lockAccount();
      return false;
    }
    const sent = await sendOtp(channel);
    if (sent) setResendCount((count) => count + 1);
    return sent;
  };

  // A3 — email OTP
  const handleVerifyEmail = async (code) => {
    const { ok, failed } = await verifyOtp(API_CHANNEL.EMAIL, code);
    if (!ok) {
      if (!failed) handleIncorrectOtp();
      return;
    }
    resetOtpState();
    setStep(STEPS.CHOOSE_CHANNEL);
  };

  // A4 / B2 — send mobile OTP on the chosen channel (WhatsApp only for now)
  const handleSendOtp = async () => {
    const sent = await sendOtp(API_CHANNEL.WHATSAPP);
    if (!sent) return;
    resetOtpState();
    setStep(STEPS.ENTER_OTP);
  };

  // Start the session. The axios interceptor only stores tokens for login/register/sso URLs,
  // so the 2FA response's tokens are saved here, then the normal post-login routing runs.
  const finishLogin = async (resp) => {
    if (sessionStarted.current) return; // auto-redirect and the button can both fire
    sessionStarted.current = true;
    const payload = getPayload(resp);
    const { accessToken, authToken, refreshToken } = payload;
    let { user } = payload;
    const token = accessToken || authToken;
    if (token) sessionStorage.setItem("accessToken", token);
    if (refreshToken) sessionStorage.setItem("refreshToken", refreshToken);
    let next = payload;
    // The 2FA responses carry only tokens; load the account so post-login routing
    // (currentStep / status) and sessionStorage.userId match a normal login
    if (!user && token) {
      try {
        const account = await globalAccount();
        next = { ...payload, ...(account?.data || {}) };
        user = next.user;
      } catch (error) {
        // fall through: routing falls back to the default landing page
      }
    }
    if (user) {
      sessionStorage.setItem("userId", JSON.stringify(user));
      identifyClarityUser(user);
    }
    onSuccess({ data: next });
  };

  // A5 / B3 — mobile OTP
  const handleVerifyMobile = async (code) => {
    const { ok, failed, resp } = await verifyOtp(API_CHANNEL.WHATSAPP, code);
    if (!ok) {
      if (!failed) handleIncorrectOtp();
      return;
    }
    resetOtpState();
    if (isFirstTime || getPayload(resp).passwordChangeRequired === true) {
      // Flow A: the session is only started after the new password is set
      pendingSession.current = resp;
      setStep(STEPS.NEW_PASSWORD);
      return;
    }
    finishLogin(resp);
  };

  // A8 — alternate number request
  const handleAlternateNumber = async ({ mobile }) => {
    // TODO(api): create CS ticket, flag account "Mobile verification pending"; use returned reference ID
    await placeholderCall();
    setAlternateRequest((prev) => ({ ...prev, mobile }));
    setStep(STEPS.REQUEST_RECEIVED);
  };

  // A10 — set a new password (POST /v1/auth/2fa/change-password)
  const handleUpdatePassword = async ({ newPassword }) => {
    setRequestError("");
    try {
      const result = await changeTwoFactorPassword({ challengeId, newPassword });
      // Success is the token pair ({ authToken, refreshToken, twoFactorEnrolled: true });
      // there is no code/status/success field on this response
      const tokens = getPayload(result);
      if (!isOk(result) && !(tokens.accessToken || tokens.authToken)) {
        setPasswordError(result?.message || "Couldn't update the password. Please try again.");
        return;
      }
      // The tokens come with whichever response carries them (change-password, else the verify step)
      pendingSession.current =
        tokens.accessToken || tokens.authToken ? result : pendingSession.current;
      setStep(STEPS.PASSWORD_UPDATED);
    } catch (error) {
      if (error instanceof Error) {
        setRequestError("Couldn't update the password. Please try again.");
      } else {
        setPasswordError(error?.message || "Couldn't update the password. Please try again.");
      }
    }
  };

  const goHome = () => finishLogin(pendingSession.current);

  const channelLabel = CHANNEL_LABELS[CHANNEL];

  const renderStep = () => {
    switch (step) {
      case STEPS.VERIFY_EMAIL:
        return (
          <OtpStep
            key={step}
            header={
              <>
                {header}
                <IconBadge className="!w-10 !h-10 mb-4">
                  <MailIcon />
                </IconBadge>
              </>
            }
            title="Verify your email"
            description={
              <>
                We&apos;ve sent a 6-digit code to{" "}
                <span className="font-geomanistBold text-blackShade">{maskedEmail}</span>.
                Enter it below to confirm this email belongs to you. Can&apos;t find it? Check your spam or promotions folder.
              </>
            }
            config={EMAIL_OTP}
            showValidity
            submitLabel="Verify email"
            error={otpError}
            errorCount={otpErrorCount}
            onClearError={() => setOtpError("")}
            onSubmit={handleVerifyEmail}
            onResend={resendFor(API_CHANNEL.EMAIL)}
            backLabel="Back to login"
            onBack={goToLogin}
          />
        );

      case STEPS.CHOOSE_CHANNEL:
        return (
          <ChooseChannel
            header={header}
            maskedMobile={maskedMobile}
            onSend={handleSendOtp}
            onBack={goToLogin}
          />
        );

      case STEPS.ENTER_OTP:
        return (
          <OtpStep
            key={step}
            header={header}
            title="Enter OTP"
            description={
              <>
                Enter the 6-digit code sent on{" "}
                <span className="inline-flex items-center gap-1 align-middle">
                  <WhatsAppIcon className="w-3.5 h-3.5 text-[#25D366]" />
                  {channelLabel}
                </span>{" "}
                to <span className="font-geomanistBold text-blackShade">{maskedMobile}</span>.
              </>
            }
            config={MOBILE_OTP}
            submitLabel={isFirstTime ? "Verify & continue" : "Verify & login"}
            error={otpError}
            errorCount={otpErrorCount}
            onClearError={() => setOtpError("")}
            onSubmit={handleVerifyMobile}
            onResend={resendFor(API_CHANNEL.WHATSAPP)}
            onBack={() => setStep(STEPS.CHOOSE_CHANNEL)}
          />
        );

      case STEPS.LOCKED:
        return <OtpLocked lockedUntil={lockedUntil} onBack={goToLogin} />;

      case STEPS.ALTERNATE_NUMBER:
        return (
          <AlternateNumber
            header={header}
            email={maskedEmail}
            maskedMobile={maskedMobile}
            onSubmit={handleAlternateNumber}
            onBack={() => setStep(STEPS.CHOOSE_CHANNEL)}
          />
        );

      case STEPS.REQUEST_RECEIVED:
        return (
          <RequestReceived
            mobile={alternateRequest.mobile}
            referenceId={alternateRequest.referenceId}
            onBack={goToLogin}
          />
        );

      case STEPS.NEW_PASSWORD:
        return (
          <SetNewPassword
            header={header}
            passwordError={passwordError}
            onClearError={() => setPasswordError("")}
            onSubmit={handleUpdatePassword}
          />
        );

      case STEPS.PASSWORD_UPDATED:
        return <PasswordUpdated email={maskedEmail} onGoHome={goHome} />;

      default:
        return null;
    }
  };

  return (
    <AuthCard>
      {requestError && (
        <Notice tone="red" className="mb-3">
          {requestError}
        </Notice>
      )}
      {renderStep()}
    </AuthCard>
  );
}

export default TwoFactor;
